import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  List, X, House, Gift, CreditCard, CurrencyBtc,
  Storefront, Info, Phone, SignIn, UserPlus, Crown, Star, Globe
} from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';
import { ThemeToggle } from './ThemeToggle';

// Global Navigation Component
export const GlobalNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // The 3 Layers + other menu items
  const menuSections = [
    {
      title: t('nav_layers'),
      items: [
        { icon: Gift, label: t('nav_gift_cards'), path: '/cartes-cadeaux', desc: t('nav_gift_cards_desc') },
        { icon: CreditCard, label: t('nav_payment_card'), path: '/carte-paiement', desc: t('nav_payment_card_desc') },
        { icon: CurrencyBtc, label: t('nav_crypto'), path: '/crypto', desc: t('nav_crypto_desc') },
      ]
    },
    {
      title: t('nav_discover'),
      items: [
        { icon: House, label: t('nav_home'), path: '/' },
        { icon: Storefront, label: t('nav_network'), path: '/reseau' },
        { icon: Crown, label: t('nav_loyalty'), path: '/fidelite' },
        { icon: Info, label: t('nav_about'), path: '/a-propos' },
        { icon: Phone, label: t('nav_contact'), path: '/contact' },
      ]
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Fixed Header */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-[9998] transition-all duration-500 ${
          scrolled 
            ? 'bg-[#0F1A14]/95 backdrop-blur-xl border-b border-[#C3A365]/20' 
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          {/* Logo */}
          <motion.button
            onClick={() => navigate('/')}
            className="brand-logo text-2xl text-white hover:text-[#C3A365] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            habitué
          </motion.button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <button onClick={() => handleNavigate('/cartes-cadeaux')} className="text-sm text-[#8BA89A] hover:text-white transition-colors">{t('nav_gift_cards')}</button>
            <button onClick={() => handleNavigate('/carte-paiement')} className="text-sm text-[#8BA89A] hover:text-white transition-colors">{t('nav_payment_card')}</button>
            <button onClick={() => handleNavigate('/crypto')} className="text-sm text-[#8BA89A] hover:text-white transition-colors">Crypto</button>
            <button onClick={() => handleNavigate('/reseau')} className="text-sm text-[#8BA89A] hover:text-white transition-colors">{t('nav_network')}</button>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <ThemeToggle className="hidden sm:flex" />
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#8BA89A] hover:text-white border border-[#3E5248] hover:border-[#C3A365]/50 transition-colors rounded-full"
              data-testid="language-toggle"
            >
              <Globe size={14} />
              <span className="font-medium">{language.toUpperCase()}</span>
            </button>

            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="text-[#8BA89A] hover:text-white text-sm font-medium transition-colors"
              >
                {t('nav_login')}
              </button>
              <button
                onClick={() => navigate('/offrir')}
                className="bg-[#C3A365] text-[#0F1A14] px-5 py-2.5 text-sm font-semibold hover:bg-[#D4B576] transition-colors flex items-center gap-2"
              >
                <Gift size={16} />
                {t('nav_gift')}
              </button>
            </div>

            {/* Hamburger */}
            <motion.button
              onClick={() => setIsOpen(true)}
              className="w-10 h-10 flex items-center justify-center text-white hover:text-[#C3A365] transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Menu"
            >
              <List size={24} weight="bold" />
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Full Screen Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#0F1A14] z-[10000] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#1E2F26]">
                <span className="brand-logo text-xl text-[#C3A365]">habitué</span>
                <div className="flex items-center gap-2">
                  {/* Theme Toggle in Menu */}
                  <ThemeToggle />
                  {/* Language Toggle in Menu */}
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#8BA89A] hover:text-white border border-[#3E5248] hover:border-[#C3A365]/50 transition-colors rounded-full"
                    data-testid="language-toggle-mobile"
                  >
                    <Globe size={14} />
                    <span className="font-medium">{language.toUpperCase()}</span>
                  </button>
                  <motion.button
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 flex items-center justify-center text-[#8BA89A] hover:text-white transition-colors"
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={24} weight="bold" />
                  </motion.button>
                </div>
              </div>

              {/* Menu Sections */}
              <nav className="p-6">
                {menuSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8">
                    <p className="text-[#C3A365] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
                      {section.title}
                    </p>
                    <div className="space-y-1">
                      {section.items.map((item, index) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <motion.button
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: (sectionIndex * 0.1) + (index * 0.05) }}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                              isActive
                                ? 'bg-[#C3A365]/10 text-[#C3A365]'
                                : 'text-white hover:bg-[#1E2F26]'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isActive ? 'bg-[#C3A365]/20' : 'bg-[#1E2F26]'
                            }`}>
                              <Icon size={20} weight={isActive ? 'fill' : 'duotone'} className={isActive ? 'text-[#C3A365]' : 'text-[#8BA89A]'} />
                            </div>
                            <div className="text-left">
                              <p className="font-medium">{item.label}</p>
                              {item.desc && <p className="text-[#8BA89A] text-xs">{item.desc}</p>}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Divider */}
                <div className="my-6 border-t border-[#1E2F26]" />

                {/* Auth Buttons */}
                <div className="space-y-3">
                  <motion.button
                    onClick={() => handleNavigate('/login')}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="w-full flex items-center justify-center gap-3 p-4 border border-[#3E5248] rounded-xl text-white hover:bg-[#1E2F26] transition-colors"
                  >
                    <SignIn size={20} />
                    <span className="font-medium">{t('nav_login')}</span>
                  </motion.button>

                  <motion.button
                    onClick={() => handleNavigate('/offrir')}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="w-full flex items-center justify-center gap-3 p-4 bg-[#C3A365] rounded-xl text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
                  >
                    <Gift size={20} />
                    <span>{t('hero_cta')}</span>
                  </motion.button>
                </div>

                {/* Global network note */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 p-4 bg-[#1E2F26] rounded-xl text-center"
                >
                  <p className="text-[#8BA89A] text-sm">
                    🌍 {t('nav_global_shops')}
                  </p>
                </motion.div>
              </nav>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[#1E2F26] bg-[#0F1A14]">
                <p className="text-[#8BA89A] text-xs text-center">
                  © 2025 habitué · {t('nav_footer')}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalNav;
